import { find, get, isFinite, kebabCase, map, mapValues, omit } from 'lodash';

const ALLOWANCE_MODE_FIELDS = ['fixedRate'];

const NON_ALLOWANCE_MODE_FIELDS = ['insuranceMonthly'];

const NUMERIC_FIELDS = [
  'fixedRate',
  'variableRate',
  'businessUsePercentage',
  'insuranceMonthly',
  'retentionYears',
  'retentionMiles',
  'cashPrice',
  'tires',
  'fuelPrice',
  'fuelMpgCity',
  'fuelMpgHighway',
  'highwayDrivingPercentage',
  'fuelMpkwHighway',
];

const COSTS_PER_YEAR_NUMERIC_FIELDS = ['repairs', 'maintenance', 'taxesAndFees', 'depreciation'];

const convertNumericFields = (obj, numericFields) => {
  return mapValues(obj, (value, key) => {
    if (numericFields.includes(key) && value !== undefined && value !== '') {
      return parseFloat(value);
    }
    return value;
  });
};

/**
 * Formats form values for API submission:
 * - Converts numeric fields from strings to numbers
 * - Excludes fields conditionated by allowanceMode when the switch is OFF
 */
export const formatFormValuesForAPI = (values, allowanceMode) => {
  let formatted = { ...values, allowanceMode };

  if (allowanceMode) {
    formatted = omit(formatted, NON_ALLOWANCE_MODE_FIELDS);
  } else {
    formatted = omit(formatted, ALLOWANCE_MODE_FIELDS);
  }

  formatted = convertNumericFields(formatted, NUMERIC_FIELDS);

  if (formatted.costsPerYear && Array.isArray(formatted.costsPerYear)) {
    formatted.costsPerYear = map(formatted.costsPerYear, yearCost =>
      convertNumericFields(yearCost, COSTS_PER_YEAR_NUMERIC_FIELDS),
    );
  }

  return omit(formatted, ['companyId', 'userId']);
};

/**
 * Builds the zmyr parameter for comparison rates API
 * Format: make-model#year#retentionYears#retentionMileage
 * @param {object} productDetails - Product details from API
 * @param {string} userId - User ID to check for custom settings
 * @param {number?} retentionMiles - Retention miles
 * @param {number?} retentionYears - Retention years
 * @param {string?} zipCode - Zip code to use for the zmyr parameter
 * @returns {string | null} - Formatted zmyr parameter or null
 */
export const buildZmyrParameter = (
  productDetails,
  userId,
  retentionMiles,
  retentionYears,
  zipCode,
) => {
  const favrSettings = get(productDetails, 'favrSettings', {});
  const standardVehicle = get(productDetails, 'standardVehicle', {});

  const make = get(standardVehicle, 'make');
  const model = get(standardVehicle, 'model');
  const year = get(standardVehicle, 'year');
  const _retentionYears = retentionYears || get(favrSettings, 'retentionYears');

  if (!make || !model || !year || !_retentionYears) {
    return null;
  }

  const standardVehicleStr = `${kebabCase(make)}-${kebabCase(model)}`.toLowerCase();

  const customSettings = get(favrSettings, 'customSettings', []);
  const userCustomSettings = find(customSettings, { userId });
  const annualBusinessMiles = userCustomSettings
    ? get(userCustomSettings, 'annualBusinessMiles', 0)
    : get(favrSettings, 'annualBusinessMiles', 0);

  const retentionMileage =
    (isFinite(parseFloat(retentionMiles))
      ? parseFloat(retentionMiles)
      : annualBusinessMiles * _retentionYears) / 1000;

  const baseZmyr = `${standardVehicleStr}#${year}#${_retentionYears}#${retentionMileage}`;

  if (zipCode) return `${zipCode}#${baseZmyr}`;
  return baseZmyr;
};

/**
 * Checks if a value is within the specified bounds (min and max, inclusive)
 * @param {number|null|undefined} value - The value to check
 * @param {Object|null|undefined} bounds - Object with min and max properties
 * @returns {boolean|null} - true if within bounds, false if out of bounds, null if cannot determine
 */
export const isWithinBounds = (value, bounds) => {
  if (!bounds || typeof bounds.min === 'undefined' || typeof bounds.max === 'undefined') {
    return null;
  }

  if (value === null || value === undefined) {
    return null;
  }

  return value >= bounds.min && value <= bounds.max;
};

/**
 * Calculates insurance bounds from comparison data based on selected insurance mode
 * @param {object} comparisonData - Comparison data from the suggestions panel
 * @param {string} insuranceMode - Selected insurance mode ('low', 'medium', or 'high')
 * @returns {object|null} - Object with min and max values, or null if no data available
 */
export const calculateInsuranceBounds = (comparisonData, insuranceMode) => {
  if (!comparisonData || !insuranceMode) {
    return null;
  }

  const values = [];

  const items = get(comparisonData, 'items', []);
  if (Array.isArray(items)) {
    items.forEach(item => {
      const insuranceValue = get(item, `cost_insurance.${insuranceMode}`);
      if (insuranceValue !== null && insuranceValue !== undefined) {
        values.push(insuranceValue);
      }
    });
  }

  if (values.length === 0) {
    return null;
  }

  const min = Math.min(...values);
  const max = Math.max(...values);

  return { min, max };
};

/**
 * Formats data for the save rate API endpoint
 * @param {object} formValues - All form field values
 * @param {object} productDetails - Product details from the API
 * @param {object|null} userDetails - User details from the API (null for new users)
 * @param {object} generatedRates - The generated rate results
 * @param {string} zmyr - The zmyr parameter
 * @param {boolean} allowanceMode - Whether allowance mode is enabled
 * @param {boolean} refreshRate - Whether to refresh the rate
 * @param {object|null} customAddress - Custom address to use instead of user's default address
 * @param {string|null} newUserEmail - Email for non-DB users
 * @returns {object} - Formatted data for save-rate endpoint
 */
export const formatSaveRateData = (
  formValues,
  productDetails,
  userDetails,
  generatedRates,
  zmyr,
  allowanceMode,
  refreshRate,
  customAddress = null,
  newUserEmail = null,
) => {
  const userEmail = newUserEmail || get(userDetails, 'email');
  const userId = get(userDetails, 'id');
  const companyId = formValues.companyId;
  const productId = formValues.productId;
  const homeAddress = customAddress || get(userDetails, 'personalInfor.homeAddress', {});
  const standardVehicle = get(productDetails, 'standardVehicle', {});
  const currency = get(productDetails, 'currency', 'USD');

  let addressNumber = get(homeAddress, 'number');
  let addressStreet = get(homeAddress, 'streetOne') || '';

  if (!addressNumber && addressStreet) {
    const parts = addressStreet.split(' ');
    if (parts.length > 1) {
      if (/^\d/.test(parts[0])) {
        // Format: "123 Main St"
        addressNumber = parts[0];
        addressStreet = parts.slice(1).join(' ');
      } else if (/^\d/.test(parts[parts.length - 1])) {
        // Format: "Main St 123"
        addressNumber = parts[parts.length - 1];
        addressStreet = parts.slice(0, -1).join(' ');
      }
    }
  }

  const saveData = {
    email: userEmail,
    userId,
    productId,
    currency,
    refreshRate,
    allowanceMode,
    zmyr,
    companyId,
    insuranceMode: formValues.insuranceMode || 'high',
    cashPrice: parseFloat(formValues.cashPrice) || undefined,
    fuelMpgCity: parseFloat(formValues.fuelMpgCity) || undefined,
    fuelMpgHighway: parseFloat(formValues.fuelMpgHighway) || undefined,
    fuelPrice: parseFloat(formValues.fuelPrice) || undefined,
    retentionMiles: parseFloat(formValues.retentionMiles) || undefined,
    retentionYears: parseInt(formValues.retentionYears, 10) || undefined,
    businessUsePercentage: parseFloat(formValues.businessUsePercentage) || undefined,
    highwayDrivingPercentage: parseFloat(formValues.highwayDrivingPercentage) || undefined,
    insuranceMonthly: parseFloat(formValues.insuranceMonthly) || undefined,
    tires: parseFloat(formValues.tires) || undefined,
    make: get(standardVehicle, 'make'),
    model: get(standardVehicle, 'model'),
    year: parseInt(get(standardVehicle, 'year'), 10),
    costsPerYear: map(formValues.costsPerYear || [], yearCost =>
      convertNumericFields(yearCost, COSTS_PER_YEAR_NUMERIC_FIELDS),
    ),
    address: {
      zip: get(homeAddress, 'postalCode') || '',
      number: addressNumber || '',
      country: get(homeAddress, 'country') || '',
      state: get(homeAddress, 'state') || '',
      city: get(homeAddress, 'city') || '',
      street: addressStreet || '',
    },
    rates: {
      variableRate: parseFloat(generatedRates.variableRate) || 0,
      variableComponents: {
        maintenance: parseFloat(get(generatedRates, 'variableComponents.maintenance')) || 0,
        repairs: parseFloat(get(generatedRates, 'variableComponents.repairs')) || 0,
        fuelPerMile: parseFloat(get(generatedRates, 'variableComponents.fuelPerMile')) || 0,
      },
      fixedRate: parseFloat(generatedRates.fixedRate) || 0,
      fixedComponents: {
        depreciation: parseFloat(get(generatedRates, 'fixedComponents.depreciation')) || 0,
        taxFees: parseFloat(get(generatedRates, 'fixedComponents.taxFees')) || 0,
        insurance: parseFloat(get(generatedRates, 'fixedComponents.insurance')) || 0,
      },
    },
  };

  if (allowanceMode) {
    return omit(saveData, NON_ALLOWANCE_MODE_FIELDS);
  }

  return saveData;
};
